import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Package, Plus, Copy, Trash2, MousePointerClick, ExternalLink } from "lucide-react";

export const CampaignLinks = ({ affiliateCode, links, onChange }) => {
  const [form, setForm] = useState({ product_name: "", product_url: "", campaign: "" });
  const [saving, setSaving] = useState(false);

  const linkUrl = (slug) => `${window.location.origin}/ref/${affiliateCode}?platform=tiktok_shop&c=${slug}`;

  const create = async () => {
    if (!form.product_name.trim()) { toast.error("Nama produk wajib diisi"); return; }
    setSaving(true);
    try {
      await api.post("/talents/me/links", form);
      toast.success("Link produk berhasil dibuat");
      setForm({ product_name: "", product_url: "", campaign: "" });
      onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/talents/me/links/${id}`);
    toast.success("Link dihapus");
    onChange();
  };

  const copy = (slug) => {
    navigator.clipboard.writeText(linkUrl(slug));
    toast.success("Link produk disalin!");
  };

  return (
    <div className="bg-card border border-border rounded-xl p-5" data-testid="campaign-links-card">
      <h3 className="font-semibold mb-1 flex items-center gap-2"><Package className="h-4 w-4 text-primary" /> Link per Produk & Kampanye</h3>
      <p className="text-xs text-muted-foreground mb-4">Buat link khusus untuk tiap produk atau kampanye agar klik bisa dilacak lebih detail.</p>

      {!affiliateCode ? (
        <p className="text-sm text-muted-foreground bg-background rounded-lg p-4 text-center" data-testid="campaign-links-locked">
          Hubungkan TikTok Shop terlebih dahulu untuk membuat link produk.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-end">
            <div>
              <Label className="text-xs">Nama Produk</Label>
              <Input data-testid="link-product-name-input" value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} placeholder="Glow Serum 30ml" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">URL Produk (opsional)</Label>
              <Input data-testid="link-product-url-input" value={form.product_url} onChange={(e) => setForm({ ...form, product_url: e.target.value })} placeholder="https://..." className="mt-1 font-mono text-xs" />
            </div>
            <div>
              <Label className="text-xs">Kampanye (opsional)</Label>
              <Input data-testid="link-campaign-input" value={form.campaign} onChange={(e) => setForm({ ...form, campaign: e.target.value })} placeholder="Promo 12.12" className="mt-1" />
            </div>
            <Button data-testid="create-campaign-link-btn" onClick={create} disabled={saving} className="bg-primary hover:bg-primary/90 text-white">
              <Plus className="h-4 w-4 mr-1" /> Buat Link
            </Button>
          </div>

          <div className="mt-5 space-y-2 max-h-80 overflow-y-auto" data-testid="campaign-links-list">
            {links.map((l) => (
              <div key={l.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-background rounded-lg p-3" data-testid={`campaign-link-row-${l.slug}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate flex items-center gap-2">
                    {l.product_name}
                    {l.campaign && <span className="text-[10px] px-2 py-0.5 rounded bg-primary/15 text-primary border border-primary/30">{l.campaign}</span>}
                  </p>
                  <p className="text-[11px] font-mono text-cyan-400 truncate">{linkUrl(l.slug)}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs font-mono flex items-center gap-1 text-amber-400" data-testid={`campaign-link-clicks-${l.slug}`}>
                    <MousePointerClick className="h-3.5 w-3.5" /> {l.clicks}
                  </span>
                  {l.product_url && (
                    <a href={l.product_url} target="_blank" rel="noopener noreferrer" className="p-2 text-muted-foreground hover:text-foreground"><ExternalLink className="h-4 w-4" /></a>
                  )}
                  <Button size="sm" variant="outline" data-testid={`copy-campaign-link-${l.slug}`} onClick={() => copy(l.slug)} className="border-border"><Copy className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" data-testid={`delete-campaign-link-${l.slug}`} onClick={() => remove(l.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            ))}
            {links.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">Belum ada link produk.</p>}
          </div>
        </>
      )}
    </div>
  );
};
